import { AS400_CDB_COLUMN } from '@erp/rpc';
import {
  normalizeUom,
  normalizePartNumber,
  type StockUomLookup,
} from '@erp/item-master-rules';
import { asCellText } from './as400ExportParts';
import {
  collectOverlayLines,
  type ItemAddLookupInputs,
  type OverlayCollectInput,
} from './itemAddPresence';

export const ITEM_STOCK_UOM_ITEM_FIELD = 'ITEMNO';
export const ITEM_STOCK_UOM_FIELD = 'ISUOM';

// The rule side (StockUomLookup, purchasingFactorMustBeOneProblem) lives in
// @erp/item-master-rules so the server checks it too.
export {
  purchasingFactorMustBeOneProblem,
  type StockUomLookup,
} from '@erp/item-master-rules';

export interface ItemStockUomLine {
  itemNumber: string;
  cdb: string;
  stockUom: string;
}

function stockUomRecordKey(
  itemNumber: string,
  cdb: string | null | undefined,
): string {
  return `${normalizePartNumber(itemNumber)}\u0000${(cdb ?? '').trim().toUpperCase()}`;
}

export function stockUomsLookup(
  lines: readonly ItemStockUomLine[],
): StockUomLookup {
  const byRecord = new Map<string, string>();
  for (const line of lines) {
    const item = normalizePartNumber(line.itemNumber);
    if (!item) continue;
    byRecord.set(stockUomRecordKey(item, line.cdb), line.stockUom);
  }
  return (itemNumber, cdb) =>
    byRecord.get(stockUomRecordKey(itemNumber, cdb)) ?? null;
}

function itemStockUomLineFromValues(
  values: Record<string, string | null | undefined>,
): ItemStockUomLine | null {
  const itemNumber = asCellText(values[ITEM_STOCK_UOM_ITEM_FIELD]);
  const stockUom = normalizeUom(asCellText(values[ITEM_STOCK_UOM_FIELD]));
  if (!itemNumber || stockUom === null) return null;
  return {
    itemNumber,
    cdb: asCellText(values[AS400_CDB_COLUMN]) ?? '',
    stockUom,
  };
}

export function collectItemStockUoms(
  input: OverlayCollectInput,
): ItemStockUomLine[] {
  return collectOverlayLines(input, itemStockUomLineFromValues, [
    ITEM_STOCK_UOM_ITEM_FIELD,
    ITEM_STOCK_UOM_FIELD,
    AS400_CDB_COLUMN,
  ]);
}

export function gridStockUoms(
  stockUoms: StockUomLookup | undefined,
  required: boolean,
): StockUomLookup | undefined {
  return required ? stockUoms : undefined;
}

export function itemSheetLookupGate(
  required: boolean,
  ready: boolean,
  error: Error | null,
): { blockSave: boolean; displayError: Error | null } {
  if (!required) return { blockSave: false, displayError: null };
  return { blockSave: !ready, displayError: error };
}

export function stockUomLookupFor(
  inputs: ItemAddLookupInputs,
): StockUomLookup | undefined {
  if (!inputs.ready) return undefined;
  return stockUomsLookup(
    collectItemStockUoms({
      rows: inputs.rows,
      newRows: inputs.newRows,
      pending: inputs.pendingScopes.flatMap((scope) => [...scope.values()]),
    }),
  );
}
